import axios from "axios";
import { getToken } from "./auth.api";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

/* ── Cache em módulo ─────────────────────────────────────────────────
   TTL 5 min: navegação entre páginas usa cache sem re-fetch.
   clearCache() é chamado pelo Dashboard a cada auto-refresh (40s)
   para garantir dados frescos quando o usuário está naquela tela.
   _pending: deduplicação — se dois componentes pedem a mesma chave
   simultaneamente, apenas UM request vai ao servidor.
──────────────────────────────────────────────────────────────────── */
const _cache   = new Map();
const _pending = new Map();
const TTL = 300_000;

async function cached(key, fn) {
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  if (_pending.has(key)) return _pending.get(key);
  const promise = fn()
    .then(data => { _cache.set(key, { data, ts: Date.now() }); _pending.delete(key); return data; })
    .catch(err  => { _pending.delete(key); throw err; });
  _pending.set(key, promise);
  return promise;
}

export function clearCache() {
  _cache.clear();
}

/* ── Endpoints ──────────────────────────────────── */

export const getSprintResumo = () =>
  cached("resumo", () => api.get("/sprint/resumo").then(r => r.data));

export const getSprintDevs = () =>
  cached("devs", () => api.get("/sprint/devs").then(r => r.data));

export const getSprintGrafico = () =>
  cached("grafico", () => api.get("/sprint/grafico").then(r => r.data));

export const getSprintDados = () =>
  cached("dados", () => api.get("/sprint/dados").then(r => Array.isArray(r.data) ? r.data : []));

export const getSprintProgramadosDetalhado = () =>
  cached("programados", () => api.get("/sprint/programados").then(r => Array.isArray(r.data) ? r.data : []));

export const getSprintHistorico = (tipo) => {
  const key = tipo ? `historico-${tipo}` : "historico";
  const url  = tipo ? `/sprint/historico?tipo=${tipo}` : "/sprint/historico";
  return cached(key, () => api.get(url).then(r => r.data));
};

export const getSprintComparativo = () =>
  cached("comparativo", () => api.get("/sprint/comparativo").then(r => r.data));

export const getProgramados = () =>
  cached("tickets-prog", async () => {
    try {
      const res = await api.get("/sprint/tickets-programacao");
      return res.data;
    } catch {
      const res = await api.get("/sprint/dados");
      return (res.data || []).filter(t => t.programado === "1" || t.programado === 1);
    }
  });

export async function atribuirTicket(ticketId, desenvolvedor, estimadoAnalise, size) {
  const body = { desenvolvedor };
  if (estimadoAnalise !== null && estimadoAnalise !== undefined && estimadoAnalise !== "")
    body.e_estimado_analise = Number(estimadoAnalise);
  if (size) body.size = size;
  const { data } = await api.put(`/tickets/${ticketId}/atribuir`, body);
  return data;
}

export async function updateTicketStatus(ticketId, status) {
  const { data } = await api.put(`/tickets/${ticketId}/status`, { status });
  return data;
}

export async function updateTicketEstimativas(ticketId, { e_estimado_analise, e_a1 }) {
  const body = {};
  if (e_estimado_analise !== null && e_estimado_analise !== "") body.e_estimado_analise = Number(e_estimado_analise);
  if (e_a1              !== null && e_a1              !== "") body.e_a1               = Number(e_a1);
  const { data } = await api.put(`/tickets/${ticketId}/estimativas`, body);
  return data;
}
